<script setup lang="ts">
/**
 * The way back from "Make personal": pick which of the reader's teams a
 * personal task, and everything under it, goes onto. A popup rather than a
 * confirm() because it is a choice, not a yes/no — and the one confirmation
 * is the Move button, since the whole team sees the tree the moment it lands.
 */
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useBoard } from "../stores/board.js";
import type { Task } from "@shared/types.js";

const props = defineProps<{ task: Task }>();
const emit = defineEmits<{ close: [] }>();

const board = useBoard();
const choice = ref<string | null>(board.teams[0]?.id ?? null);
const saving = ref(false);
const serverError = ref<string | null>(null);

/** Everything under it goes too; saying how much is the point of asking. */
const under = computed(() => board.rollups.get(props.task.id)?.descendantCount ?? 0);

async function move(): Promise<void> {
  if (!choice.value || saving.value) return;
  saving.value = true;
  serverError.value = null;
  try {
    await board.makeShared(props.task.id, { teamId: choice.value });
    emit("close");
  } catch (err) {
    serverError.value = (err as Error).message;
  } finally {
    saving.value = false;
  }
}

function onKeydown(ev: KeyboardEvent): void {
  if (ev.key === "Escape") {
    ev.stopPropagation();
    emit("close");
  }
}

onMounted(() => window.addEventListener("keydown", onKeydown));
onBeforeUnmount(() => window.removeEventListener("keydown", onKeydown));
</script>

<template>
  <Teleport to="body">
    <div class="modal-backdrop" @pointerdown.self="emit('close')">
      <form class="modal move-team-modal" role="dialog" aria-modal="true" aria-labelledby="move-team-title" @submit.prevent="move">
        <header class="modal-head">
          <span id="move-team-title">Move to a team</span>
          <span style="flex: 1"></span>
          <button class="btn ghost" type="button" title="Close" aria-label="Close" @click="emit('close')">✕</button>
        </header>

        <div class="modal-body">
          <p class="modal-lede">
            “{{ task.title }}”<template v-if="under"> and the {{ under }} task{{ under === 1 ? "" : "s" }} under it</template>
            will leave your personal tasks. Everyone in the team will see
            {{ under ? "them" : "it" }}.
          </p>
          <p v-if="!board.teams.length" class="modal-lede">You are not in a team.</p>
          <div v-else class="move-team-list" role="radiogroup" aria-label="Team">
            <label v-for="t in board.teams" :key="t.id" class="move-team-option" :class="{ on: choice === t.id }">
              <input v-model="choice" type="radio" name="move-team" :value="t.id" />
              <span class="move-team-name">{{ t.name }}</span>
              <span class="move-team-count">{{ t.memberIds.length }} {{ t.memberIds.length === 1 ? "person" : "people" }}</span>
            </label>
          </div>
        </div>

        <footer class="modal-foot">
          <span v-if="serverError" class="field-problem modal-error">{{ serverError }}</span>
          <span style="flex: 1"></span>
          <button class="btn ghost" type="button" @click="emit('close')">Cancel</button>
          <button class="btn primary" type="submit" :disabled="saving || !choice">
            {{ saving ? "Moving…" : "Move" }}
          </button>
        </footer>
      </form>
    </div>
  </Teleport>
</template>

